import React, { useEffect, useState } from 'react';
import { formatLocalDate } from '../../../utils/time';

const weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const today = new Date();

interface BookingCalendarProps {
  selectedDate: string; // ISO 'YYYY-MM-DD' or ''
  onSelectDate: (iso: string) => void;
  isDateDisabled?: (iso: string, date: Date) => boolean;
  onViewMonthChange?: (year: number, month: number) => void; // month = 1-12
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({
  selectedDate,
  onSelectDate,
  isDateDisabled,
  onViewMonthChange,
}) => {
  const [viewDate, setViewDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDayOfMonth = viewDate.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const month1Based = month + 1;
  useEffect(() => {
    onViewMonthChange?.(year, month1Based);
  }, [year, month1Based, onViewMonthChange]);

  const dayCells = Array.from({ length: firstDayOfMonth + daysInMonth }, (_, index) => {
    if (index < firstDayOfMonth) return null;
    return index - firstDayOfMonth + 1;
  });

  const goToPreviousMonth = () => setViewDate(new Date(year, month - 1, 1));
  const goToNextMonth = () => setViewDate(new Date(year, month + 1, 1));

  return (
    <div className="booking-calendar mt-1 mb-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <button
          type="button"
          disabled={year === today.getFullYear() && month === today.getMonth()}
          className="btn btn-outline-secondary btn-sm"
          onClick={goToPreviousMonth}
        >
          Previous month
        </button>
        <strong>{monthLabel}</strong>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goToNextMonth}>
          Next month
        </button>
      </div>
      <div className="booking-calendar-grid booking-calendar-weekdays">
        {weekdayLabels.map((label) => (
          <div key={label} className="booking-calendar-cell booking-calendar-weekday">
            {label}
          </div>
        ))}
      </div>
      <div className="booking-calendar-grid">
        {dayCells.map((day, index) => {
          if (!day) {
            return <div key={`empty-${index}`} className="booking-calendar-cell booking-calendar-empty" />;
          }
          const date = new Date(year, month, day);
          const isoDate = formatLocalDate(date);
          const isSelected = selectedDate === isoDate;
          const isPastDayInCurrentMonth =
            year === today.getFullYear() && month === today.getMonth() && day < today.getDate();
          const disabled = isPastDayInCurrentMonth || (isDateDisabled?.(isoDate, date) ?? false);
          return (
            <button
              key={isoDate}
              type="button"
              className={`booking-calendar-cell booking-calendar-day ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
              disabled={disabled}
              onClick={() => onSelectDate(isoDate)}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BookingCalendar;
